import type { HTMLChakraProps } from '@chakra-ui/react';
import { chakra } from '@chakra-ui/react';
import { type IconName } from 'public/icons/name';
import React from 'react';

import { getEnvValue } from 'configs/app/utils';
import { Skeleton } from 'toolkit/chakra/skeleton';

// IMPORTANT: Use a function to get the sprite hash at runtime, not at module load time
// This ensures that the hash is read from window.__envs in the browser
const getSpriteHref = () => {
  const spriteHash = getEnvValue('NEXT_PUBLIC_ICON_SPRITE_HASH');
  return spriteHash ? `/icons/sprite.${ spriteHash }.svg` : '/icons/sprite.svg';
};

// Export href for use in _document.tsx preload link
// This will be evaluated at module load time on the server (SSR)
// and will use process.env.NEXT_PUBLIC_ICON_SPRITE_HASH
export const href = getSpriteHref();

export { IconName };

export interface Props extends HTMLChakraProps<'div'> {
  name: IconName;
  isLoading?: boolean;
}

const IconSvg = React.forwardRef(
  function IconSvg({ name, isLoading = false, ...props }: Props, ref: React.ForwardedRef<HTMLDivElement>) {
    // Remove slashes from icon name to match sprite file IDs
    // svg-icons-cli tool converts "social/discord.svg" to "socialdiscord" in the sprite
    const iconId = name.replace(/\//g, '');

    // Get sprite href at render time, not at module load time
    const href = getSpriteHref();

    return (
      <Skeleton loading={ isLoading } display="inline-block" flexShrink={ 0 } asChild { ...props } ref={ ref }>
        <chakra.svg w="100%" h="100%">
          <use href={ `${ href }#${ iconId }` }/>
        </chakra.svg>
      </Skeleton>
    );
  },
);

IconSvg.displayName = 'IconSvg';

export default IconSvg;
